import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { Building2, Briefcase, HardHat, Megaphone, Landmark, Sparkles, Wallet } from 'lucide-react';
import { SECTION_SLUGS } from '@massar/shared/constants';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { useAuth } from '@/features/auth/AuthContext';
import { useOnboarding } from './OnboardingProvider';
import { TOUR, WELCOME_STEPS, SECTION_GUIDE } from './tours';
import { cn } from '@/lib/utils';

/**
 * The first-login walkthrough: **what this system is, and which section does what.**
 *
 * The single most-asked question about a multi-section ERP is not "where is the button" —
 * it is "I have an invoice for a shop fit-out; is that Construction or Agency?" The
 * sidebar cannot answer that, because the sidebar lists screens and the question is about
 * the business. So this leads with the four business lines and what each one *does*,
 * before it mentions a single screen.
 *
 * A dialog rather than coach marks, and that is the point of the split: at first login
 * there is nothing on screen worth pointing at, and a spotlight on an empty dashboard
 * teaches the user where a link is when they do not yet know what it is for. Screen tours
 * (`ScreenTour`) do the pointing, later, on the screens that have something to point at.
 */

const SECTION_ICONS = {
  [SECTION_SLUGS.REAL_ESTATE]: Building2,
  [SECTION_SLUGS.AGENCY]: Megaphone,
  [SECTION_SLUGS.BROKERS]: Briefcase,
  [SECTION_SLUGS.CONSTRUCTION]: HardHat,
  [SECTION_SLUGS.CLEANER]: Sparkles,
};

const SECTION_ACCENT = {
  [SECTION_SLUGS.REAL_ESTATE]: 'var(--section-realestate)',
  [SECTION_SLUGS.AGENCY]: 'var(--section-agency)',
  [SECTION_SLUGS.BROKERS]: 'var(--section-brokers)',
  [SECTION_SLUGS.CONSTRUCTION]: 'var(--section-construction)',
  [SECTION_SLUGS.CLEANER]: 'var(--section-cleaner)',
};

export const WelcomeTour = () => {
  const { t } = useTranslation(['onboarding', 'common']);
  const { user } = useAuth();
  const { active, finish } = useOnboarding();
  const [index, setIndex] = React.useState(0);

  const open = active === TOUR.WELCOME;
  const step = WELCOME_STEPS[index];
  const isLast = index === WELCOME_STEPS.length - 1;

  // Start from the top whenever it is (re)opened, including a replay from the help menu.
  React.useEffect(() => {
    if (open) setIndex(0);
  }, [open]);

  if (!open || !step) return null;

  const close = (action) => finish(TOUR.WELCOME, action);

  return (
    <Dialog open onOpenChange={(next) => !next && close('skip')}>
      {/* The tour paints edge to edge — its own gradient header supplies the padding. */}
      <DialogContent className="max-w-2xl" bodyClassName="gap-0 p-0" showClose={false}>
        <DialogTitle className="sr-only">{t('onboarding:welcome.a11yTitle')}</DialogTitle>

        <div className="bg-gradient-to-br from-primary/10 via-transparent to-transparent px-8 pt-8 pb-6">
          <p className="text-caption font-medium uppercase tracking-widest text-muted-foreground">
            {t('onboarding:welcome.eyebrow', { step: index + 1, total: WELCOME_STEPS.length })}
          </p>
          <h2 className="mt-2 text-2xl font-semibold tracking-tight">
            {index === 0
              ? t('onboarding:welcome.intro.title', { name: user?.name?.split(' ')[0] ?? '' })
              : t(`onboarding:${step.title}`)}
          </h2>
          <p className="mt-3 max-w-prose text-base leading-relaxed text-muted-foreground">
            {t(`onboarding:${step.body}`)}
          </p>
        </div>

        <div className="min-h-[9rem] px-8 pb-2">
          {step.render === 'sections' ? <SectionsStep /> : null}
          {step.render === 'safes' ? <SafesStep /> : null}
        </div>

        <div className="flex items-center justify-between gap-4 border-t bg-muted/30 px-8 py-4">
          {/* Progress as dots, not a bar: six steps is countable, and a dot the user has
              passed is clickable, which a bar segment is not. */}
          <div className="flex items-center gap-1.5">
            {WELCOME_STEPS.map((s, i) => (
              <button
                key={s.id}
                type="button"
                aria-label={t('onboarding:welcome.goToStep', { step: i + 1 })}
                aria-current={i === index ? 'step' : undefined}
                onClick={() => setIndex(i)}
                className={cn(
                  'h-2 rounded-full transition-all',
                  i === index ? 'w-6 bg-primary' : 'w-2 bg-border hover:bg-muted-foreground/40',
                )}
              />
            ))}
          </div>

          <div className="flex items-center gap-2">
            <Button variant="ghost" onClick={() => close('skip')}>
              {t('onboarding:actions.skip')}
            </Button>
            {index > 0 ? (
              <Button variant="outline" onClick={() => setIndex((i) => i - 1)}>
                {t('common:actions.back')}
              </Button>
            ) : null}
            <Button onClick={() => (isLast ? close('complete') : setIndex((i) => i + 1))}>
              {isLast ? t('onboarding:actions.done') : t('common:actions.next')}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

/**
 * The four business lines, each with one sentence on what it is for and one on what it
 * bills. Rendered from `SECTION_GUIDE`, so a card cannot describe a section the system
 * does not have — and each carries its own accent colour, which is the same colour the
 * section switcher and the active nav bar will use, so the association starts here.
 */
const SectionsStep = () => {
  const { t } = useTranslation('onboarding');

  return (
    <div className="grid gap-3 sm:grid-cols-2">
      {SECTION_GUIDE.map(({ slug, key }) => {
        const Icon = SECTION_ICONS[slug];
        return (
          <div
            key={slug}
            className="rounded-xl border bg-card p-4 transition-shadow hover:shadow-sm"
            style={{ borderInlineStartWidth: 3, borderInlineStartColor: `hsl(${SECTION_ACCENT[slug]})` }}
          >
            <div className="flex items-center gap-2">
              <Icon className="size-4" style={{ color: `hsl(${SECTION_ACCENT[slug]})` }} />
              <p className="font-semibold">{t(`sections.${key}.name`)}</p>
            </div>
            <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">
              {t(`sections.${key}.does`)}
            </p>
            <p className="mt-2 text-caption text-muted-foreground">
              <span className="font-medium text-foreground">{t('sections.billsLabel')}</span>{' '}
              {t(`sections.${key}.bills`)}
            </p>
          </div>
        );
      })}
    </div>
  );
};

/**
 * The safe hierarchy, drawn rather than described.
 *
 * "Every section safe is a child of the Main Safe" is the rule that explains why a
 * section cannot simply pay another section, and it is far easier to see than to read.
 */
const SafesStep = () => {
  const { t } = useTranslation('onboarding');

  return (
    <div className="space-y-3">
      <div className="mx-auto flex w-fit items-center gap-2 rounded-xl border-2 border-primary/30 bg-primary/5 px-4 py-2.5">
        <Landmark className="size-4 text-primary" />
        <span className="font-semibold">{t('safes.main')}</span>
      </div>

      <div className="flex justify-center" aria-hidden="true">
        <span className="h-4 w-px bg-border" />
      </div>

      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        {SECTION_GUIDE.map(({ slug, key }) => (
          <div key={slug} className="rounded-lg border bg-card px-3 py-2 text-center">
            <Wallet className="mx-auto size-3.5" style={{ color: `hsl(${SECTION_ACCENT[slug]})` }} />
            <p className="mt-1 text-caption font-medium">{t(`sections.${key}.name`)}</p>
          </div>
        ))}
      </div>

      <p className="text-center text-caption text-muted-foreground">{t('safes.rule')}</p>
    </div>
  );
};

export default WelcomeTour;
